import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Col, Input, Row } from "reactstrap";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import api from "services/api";
import obterUserNome from "utils/userNome";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";
import CotacaoListItem from "./components/CotacaoListItem";
import FinancialPanel from "./components/FinancialPanel";
import EditCotacaoModal from "./components/EditCotacaoModal";
import FreteCombinadoModal from "./components/FreteCombinadoModal";
import { QUOTE_STATUS_OPTIONS } from "./statusOptions";
import { endOfDay, parseDateLike, startOfDay } from "../../../utils/dateOnly";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import { leiloesAtivos } from "services/leilaoFreteApi"; // PO1-3084 (épico PO1-3076)
import "./components/listagem.css";

const LIST_PAGE_SIZE = 20;

const heroShellStyle = {
  ...autopilotCardStyle,
  display: "flex",
  flexDirection: "column",
  background: "var(--everlog-card-hero-bg)",
};

const SectionShell = ({
  className = "",
  eyebrow,
  title,
  description,
  actions = null,
  children,
}) => {
  const hasHeaderCopy = eyebrow || title || description;
  const hasStandaloneActions = actions && !hasHeaderCopy;

  return (
    <Card className={`card-everlog-shell ${className}`.trim()} style={heroShellStyle}>
      {hasHeaderCopy ? (
        <CardHeader
          className={`lc-shell__header${actions ? " lc-shell__header--split" : ""}`}
          style={autopilotHeaderStyle}
        >
          <div>
            {eyebrow ? <div style={autopilotEyebrowStyle}>{eyebrow}</div> : null}
            {title ? <div style={autopilotTitleStyle}>{title}</div> : null}
            {description ? <p style={autopilotDescriptionStyle}>{description}</p> : null}
          </div>
          {actions ? <div className="lc-shell__actions">{actions}</div> : null}
        </CardHeader>
      ) : null}
      <CardBody className="lc-shell__body" style={autopilotBodyStyle}>
        {hasStandaloneActions ? (
          <div className="lc-shell__actions lc-shell__actions--standalone">{actions}</div>
        ) : null}
        {children}
      </CardBody>
    </Card>
  );
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  const adjustedStart = Math.max(1, end - 4);

  return Array.from(
    { length: end - adjustedStart + 1 },
    (_, index) => adjustedStart + index
  );
};

const ListagemCotacoes = () => {
  const history = useHistory();
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa?.value;
  const { addToast } = useToast();

  const [embarques, setEmbarques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmb, setSelectedEmb] = useState(null);
  const [editEmb, setEditEmb] = useState(null);
  // PO1-3488: "Cotar" abre o modal já na seção de frete; "Abrir", no topo.
  const [focoEdit, setFocoEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [combinadoOpen, setCombinadoOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // PO1-2713 — o usuário logado decide aprovações acima do orçado?
  const [isAprovador, setIsAprovador] = useState(false);
  // PO1-3084 — selo "Em leilão": baseNumEmbarque → { leilaoId, numero, estado }
  // das ofertas vivas (aberta / aguardando escolha) do embarcador.
  const [leiloesPorBase, setLeiloesPorBase] = useState({});
  // Deep link ?abrir=<id> (link do e-mail da alçada): lido uma vez no mount,
  // consumido depois que a lista carrega.
  const abrirRef = useRef(new URLSearchParams(history.location.search).get("abrir"));
  const loadedOnceRef = useRef(false);

  // Filters
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTransportadora, setFilterTransportadora] = useState("");
  // PO1-2712 — deep link ?uf=<sigla> (clique na tabela do mapa Frete por
  // Estado): pré-configura o filtro de UF, lido uma vez no mount como o
  // ?abrir acima.
  const [filterUF, setFilterUF] = useState(() => {
    const uf = new URLSearchParams(history.location.search).get("uf");
    // Mesma sanitização do input de UF (2 letras maiúsculas).
    return uf ? uf.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) : "";
  });
  const [filterDataInicio, setFilterDataInicio] = useState("");
  const [filterDataFim, setFilterDataFim] = useState("");

  const loadEmbarques = useCallback(async ({ noCache = false } = {}) => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const response = await api.get(`/embarcadores/embarques/${empresaId}`,
        noCache ? { params: { noCache: 1, _: Date.now() } } : undefined);
      const rows = Array.isArray(response.data) ? response.data : [];
      // Sort newest first
      const sorted = [...rows].sort((a, b) => {
        const da = new Date(a.dataEmbarque || 0);
        const db = new Date(b.dataEmbarque || 0);
        return db - da;
      });
      setEmbarques(sorted);
    } catch {
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: "Falha ao carregar cotações.",
      });
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
    }
    // PO1-3084: selo "Em leilão", best-effort (falha = sem selo, a lista segue).
    leiloesAtivos(empresaId)
      .then((r) => setLeiloesPorBase((r && r.porBase) || {}))
      .catch(() => setLeiloesPorBase({}));
  }, [empresaId]); // eslint-disable-line

  // PO1-3084: "Leiloar" leva à tela de Leilões já com a cotação (ou abre o
  // leilão vivo dela).
  const handleLeiloar = useCallback((emb, ativo) => {
    if (!emb) return;
    if (ativo && ativo.leilaoId) {
      history.push(`/app/gobid/operacao/leiloes?abrir=${ativo.leilaoId}`);
      return;
    }
    history.push(`/app/gobid/operacao/leiloes?cotacao=${emb._id}`);
  }, [history]);
  const leilaoAtivoDe = useCallback(
    (emb) => (emb ? leiloesPorBase[emb.baseNumEmbarque || emb.numEmbarque] || null : null),
    [leiloesPorBase],
  );

  useEffect(() => {
    // Vindo de "Salvar Cotação" (NovaCotacaoManual passa state.fresh) →
    // 1ª carga ignora o cache (3 min) p/ a cotação recém-criada aparecer
    // na hora. Depois limpa o state p/ refresh manual usar cache normal.
    const veioDoSalvar = !!(history.location.state && history.location.state.fresh);
    loadEmbarques({ noCache: veioDoSalvar });
    if (veioDoSalvar) {
      history.replace({ pathname: history.location.pathname, state: null });
    }
  }, [loadEmbarques]); // eslint-disable-line

  useEffect(() => {
    if (hasEmpresaSelection) {
      return;
    }

    setEmbarques([]);
    setSelectedEmb(null);
    setEditEmb(null);
  }, [hasEmpresaSelection]);

  // PO1-2713 — contexto da alçada p/ o embarcador selecionado. Erro = não
  // aprovador (a decisão real é do backend; aqui só controla os botões).
  useEffect(() => {
    if (!empresaId) {
      setIsAprovador(false);
      return undefined;
    }
    let cancelled = false;
    api.get(`/embarcadores/embarques/alcada/${empresaId}`)
      .then(({ data }) => {
        if (!cancelled) setIsAprovador(!!(data && data.aprovador));
      })
      .catch(() => {
        if (!cancelled) setIsAprovador(false);
      });
    return () => { cancelled = true; };
  }, [empresaId]);

  const filteredEmbarques = useMemo(() => {
    return embarques.filter((emb) => {
      if (filterStatus && emb.statusEmbarque !== filterStatus) return false;
      if (
        filterTransportadora &&
        !(emb.transportadora || "")
          .toLowerCase()
          .includes(filterTransportadora.toLowerCase())
      )
        return false;
      if (filterUF && (emb.ufDestino || "").toUpperCase() !== filterUF.toUpperCase())
        return false;
      if (filterDataInicio) {
        const d = parseDateLike(emb.dataEmbarque);
        const start = startOfDay(filterDataInicio);
        if (!d || !start || d < start) return false;
      }
      if (filterDataFim) {
        const d = parseDateLike(emb.dataEmbarque);
        const end = endOfDay(filterDataFim);
        if (!d || !end || d > end) return false;
      }
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const haystack = [
          emb.numeroCotacao != null ? String(emb.numeroCotacao) : null, // PO1-2863
          emb.numEmbarque,
          emb.codigoPedido,
          emb.cliente,
          emb.cidadeDestino,
          emb.ufDestino,
          emb.transportadora,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [
    embarques,
    filterStatus,
    filterTransportadora,
    filterUF,
    filterDataInicio,
    filterDataFim,
    filterSearch,
  ]);

  // Status summary counts
  const statusCounts = useMemo(() => {
    const counts = { Pendente: 0, "Stand-by": 0, Simulado: 0, Realizado: 0 };
    embarques.forEach((e) => {
      if (counts[e.statusEmbarque] !== undefined) counts[e.statusEmbarque]++;
    });
    return counts;
  }, [embarques]);

  const activeFilterCount = useMemo(
    () =>
      [
        filterSearch,
        filterStatus,
        filterTransportadora,
        filterUF,
        filterDataInicio,
        filterDataFim,
      ].filter(Boolean).length,
    [
      filterDataFim,
      filterDataInicio,
      filterSearch,
      filterStatus,
      filterTransportadora,
      filterUF,
    ]
  );

  const hasActiveFilters = activeFilterCount > 0;
  const totalPages = Math.max(1, Math.ceil(filteredEmbarques.length / LIST_PAGE_SIZE));
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );
  const paginatedEmbarques = useMemo(() => {
    const startIndex = (currentPage - 1) * LIST_PAGE_SIZE;
    return filteredEmbarques.slice(startIndex, startIndex + LIST_PAGE_SIZE);
  }, [currentPage, filteredEmbarques]);

  const STATUS_COLORS = {
    Pendente: "var(--everlog-status-neutral)",
    "Stand-by": "var(--everlog-status-warning)",
    Simulado: "var(--everlog-status-info)",
    Realizado: "var(--everlog-status-success)",
  };

  const handleOpenFull = useCallback(async (emb) => {
    // A listagem é projetada (sem veiculosMix/cotacoes/historico). Abrir o
    // modal com esse objeto fazia o save reescrever esses arrays vazios e
    // destruir os dados. Abre já com o parcial (responsivo) e substitui
    // pelo doc COMPLETO assim que chega. A rede de segurança no backend
    // cobre a janela curta até o fetch completar.
    setEditEmb(emb);
    if (!emb || !emb._id) return;
    try {
      const { data } = await api.get(`/embarcadores/embarques/by-id/${emb._id}`);
      if (data && data._id) {
        setEditEmb((cur) => (cur && cur._id === data._id ? data : cur));
      }
    } catch (err) {
      // Mantém o parcial aberto; a rede de segurança do backend impede que
      // um save apague veiculosMix/cotacoes.
      console.error("[ListagemCotacoes] falha ao carregar cotação completa:", err);
    }
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditEmb(null);
    setFocoEdit(null);
  }, []);

  const handleAbrir = useCallback((emb) => {
    setFocoEdit(null);
    handleOpenFull(emb);
  }, [handleOpenFull]);

  const handleCotar = useCallback((emb) => {
    setFocoEdit("frete");
    handleOpenFull(emb);
  }, [handleOpenFull]);

  const handleSaveEdit = useCallback(async (id, payload) => {
    if (!empresaId) {
      return;
    }
    setSaving(true);
    try {
      const putResponse = await api.put(`/embarcadores/embarques/${id}`, payload);
      const data = putResponse && putResponse.data ? putResponse.data : null;
      if (data && data.aprovacaoPendente) {
        // PO1-2713: a alçada barrou a aprovação (HTTP 200, status segue
        // Stand-by). Os dados editados foram salvos; falta o aprovador.
        const alcada = data.alcada || {};
        const fmt = (v) => (v === null || v === undefined || Number.isNaN(Number(v))
          ? "—"
          : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
        const pct = (v) => (v === null || v === undefined || Number.isNaN(Number(v))
          ? "—"
          : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`);
        addToast({
          place: "tr",
          type: "warning",
          icon: "tim-icons icon-alert-circle-exc",
          title: "Enviado para aprovação",
          description:
            `Valor ${fmt(alcada.valorNovo)} acima do orçado ${fmt(alcada.valorReferencia)} ` +
            `(diferença ${fmt(alcada.diferenca)}, ${pct(alcada.diferencaPct)}; tolerância ${pct(alcada.toleranciaPct)}). ` +
            "A cotação segue em Stand-by até a decisão do aprovador.",
        });
      } else {
        addToast({
          place: "tr",
          type: "success",
          icon: "tim-icons icon-check-2",
          title: "Salvo",
          description: "Cotação atualizada com sucesso.",
        });
      }
      setEditEmb(null);
      // Atualiza a lista e o painel lateral com os novos dados. noCache=1:
      // força leitura fresca do Mongo (não confia só na invalidação por
      // pattern nem em cache de browser) logo após a mutação.
      const response = await api.get(`/embarcadores/embarques/${empresaId}`, {
        params: { noCache: 1, _: Date.now() },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      const sorted = [...rows].sort((a, b) => new Date(b.dataEmbarque || 0) - new Date(a.dataEmbarque || 0));
      setEmbarques(sorted);
      setSelectedEmb((prev) => {
        if (!prev) return prev;
        const fresh = sorted.find((e) => e._id === prev._id);
        return fresh || prev;
      });
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : "Falha ao salvar. Tente novamente.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, loadEmbarques, empresaId]); // eslint-disable-line

  // Exclusão (hard delete — backend DELETE /embarcadores/embarques/:id).
  // Irreversível; a confirmação fica no EditCotacaoModal (2 passos).
  const handleDeleteEmbarque = useCallback(async (id) => {
    if (!empresaId || !id) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/embarcadores/embarques/${id}`);
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: "Excluída",
        description: "Cotação excluída.",
      });
      setEditEmb(null);
      const response = await api.get(`/embarcadores/embarques/${empresaId}`, {
        params: { noCache: 1, _: Date.now() },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(b.dataEmbarque || 0) - new Date(a.dataEmbarque || 0)
      );
      setEmbarques(sorted);
      setSelectedEmb((prev) => (prev && prev._id === id ? null : prev));
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : "Falha ao excluir. Tente novamente.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, empresaId]); // eslint-disable-line

  // PO1-2783 — versão REAL: o backend clona a cotação Aprovada num doc novo
  // (versionNumber n+1, Stand-by, previousVersionId) e marca a anterior como
  // não corrente. A resposta (201) já vem normalizada e completa (com
  // historico/cotacoes/veiculosMix), então abre o modal direto nela.
  const handleCreateVersion = useCallback(async (emb) => {
    if (!empresaId || !emb?._id) {
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post(`/embarcadores/embarques/${emb._id}/versoes`);

      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: data && data.versionLabel ? `Versão ${data.versionLabel} criada` : "Versão criada",
        description: "A nova versão nasceu em Stand-by com todos os dados copiados e está liberada para edição.",
      });

      const response = await api.get(`/embarcadores/embarques/${empresaId}`, {
        params: { noCache: 1, _: Date.now() },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(b.dataEmbarque || 0) - new Date(a.dataEmbarque || 0)
      );

      setEmbarques(sorted);
      setSelectedEmb(data && data._id ? data : null);
      setEditEmb(data && data._id ? data : null);
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : "Falha ao criar nova versão. Tente novamente.";

      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, empresaId]);

  // PO1-2713 — decisão do aprovador. O backend valida quem pode (403
  // NAO_APROVADOR) e se há pendência (409). Sucesso → recarrega a lista e
  // mantém o modal aberto no doc devolvido (já com historico/aprovacao).
  const decidirAlcada = useCallback(async (id, acao, body, titulo, descricao) => {
    if (!empresaId || !id) {
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/embarcadores/embarques/${id}/aprovacao/${acao}`,
        { userNome: obterUserNome(), ...body }
      );
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: titulo,
        description: descricao,
      });
      const response = await api.get(`/embarcadores/embarques/${empresaId}`, {
        params: { noCache: 1, _: Date.now() },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(b.dataEmbarque || 0) - new Date(a.dataEmbarque || 0)
      );
      setEmbarques(sorted);
      const fresh = data && data._id ? data : sorted.find((e) => e._id === id) || null;
      setEditEmb((cur) => (cur && cur._id === id && fresh ? fresh : cur));
      setSelectedEmb((prev) => {
        if (!prev) return prev;
        const item = sorted.find((e) => e._id === prev._id);
        return item || prev;
      });
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : "Falha ao registrar a decisão. Tente novamente.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, empresaId]);

  const handleAprovar = useCallback(
    (id) => decidirAlcada(id, "aprovar", {}, "Cotação aprovada", "Aprovação acima do orçado registrada; a transportadora foi notificada."),
    [decidirAlcada]
  );

  const handleRecusar = useCallback(
    (id, justificativa) => decidirAlcada(id, "recusar", { justificativa }, "Cotação recusada", "A cotação permanece em Stand-by; o solicitante foi notificado."),
    [decidirAlcada]
  );

  // PO1-2792 — pede a coleta à transportadora. O backend valida o status
  // (409 COLETA_SOMENTE_APROVADA), a versão (403 VERSAO_ANTIGA_SOMENTE_LEITURA)
  // e o envio (502 COLETA_NAO_ENVIADA, com o motivo na mensagem). Sucesso →
  // doc normalizado com coleta/coletaSolicitada; o modal segue aberto nele.
  // PO1-2576: opcoes = { dataColeta, canal }. Pela API o backend grava o
  // estado (solicitada / dry-run / aguardando-etiqueta / falha) e responde 200
  // mesmo na falha — o toast segue o status gravado.
  const handleSolicitarColeta = useCallback(async (id, opcoes) => {
    if (!empresaId || !id) {
      return;
    }
    const o = opcoes || {};
    setSaving(true);
    try {
      const body = { userNome: obterUserNome() };
      if (o.dataColeta) body.dataColeta = o.dataColeta;
      if (o.canal) body.canal = o.canal;
      const { data } = await api.post(`/embarcadores/embarques/${id}/coleta/solicitar`, body);
      const coleta = (data && data.coleta) || {};
      const destinatarios = Array.isArray(coleta.destinatarios) ? coleta.destinatarios.filter(Boolean) : [];
      const destino = (data && data.transportadora) || destinatarios.join(", ") || "a transportadora";
      if (coleta.canal === "api" && coleta.status === "falha") {
        addToast({
          place: "tr",
          type: "warning",
          icon: "tim-icons icon-alert-circle-exc",
          title: "Coleta não solicitada",
          description: coleta.erro || `${destino} recusou a coleta pela integração. Tente de novo ou envie por e-mail.`,
        });
      } else if (coleta.canal === "api" && coleta.status === "aguardando-etiqueta") {
        addToast({
          place: "tr",
          type: "info",
          icon: "tim-icons icon-send",
          title: "Coleta aguardando a etiqueta",
          description: "A coleta dos Correios é pedida depois de gerar a etiqueta na NF (Consulta de NF-e, aba Etiqueta).",
        });
      } else if (coleta.canal === "api") {
        addToast({
          place: "tr",
          type: "success",
          icon: "tim-icons icon-send",
          title: coleta.status === "dry-run" ? "Coleta simulada (modo teste)" : "Coleta solicitada",
          description: coleta.status === "dry-run"
            ? `Vínculo em modo teste: nada foi enviado a ${destino}.`
            : `Coleta nº ${coleta.numero || "—"} pedida pela integração com ${destino}.`,
        });
      } else {
        addToast({
          place: "tr",
          type: "success",
          icon: "tim-icons icon-send",
          title: "Coleta solicitada",
          description: `Solicitação de coleta enviada para ${destino}.`,
        });
      }
      const response = await api.get(`/embarcadores/embarques/${empresaId}`, {
        params: { noCache: 1, _: Date.now() },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(b.dataEmbarque || 0) - new Date(a.dataEmbarque || 0)
      );
      setEmbarques(sorted);
      const fresh = data && data._id ? data : sorted.find((e) => e._id === id) || null;
      setEditEmb((cur) => (cur && cur._id === id && fresh ? fresh : cur));
      setSelectedEmb((prev) => {
        if (!prev) return prev;
        const item = sorted.find((e) => e._id === prev._id);
        return item || prev;
      });
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : "Não foi possível enviar a solicitação de coleta.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, empresaId]);

  // PO1-3399 — cancela a coleta já pedida: o backend envia o e-mail de
  // cancelamento aos mesmos destinatários e só então grava (502 = nada mudou).
  // Devolve true no sucesso para o modal fechar o formulário do motivo.
  const handleCancelarColeta = useCallback(async (id, motivo) => {
    if (!empresaId || !id) {
      return false;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/embarcadores/embarques/${id}/coleta/cancelar`,
        { motivo, userNome: obterUserNome() }
      );
      const destinatarios = data && data.coletaCancelamento && Array.isArray(data.coletaCancelamento.destinatarios)
        ? data.coletaCancelamento.destinatarios.filter(Boolean)
        : [];
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: "Coleta cancelada",
        description: `Cancelamento enviado para ${destinatarios.join(", ") || "a transportadora"}.`,
      });
      const response = await api.get(`/embarcadores/embarques/${empresaId}`, {
        params: { noCache: 1, _: Date.now() },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(b.dataEmbarque || 0) - new Date(a.dataEmbarque || 0)
      );
      setEmbarques(sorted);
      const fresh = data && data._id ? data : sorted.find((e) => e._id === id) || null;
      setEditEmb((cur) => (cur && cur._id === id && fresh ? fresh : cur));
      setSelectedEmb((prev) => {
        if (!prev) return prev;
        const item = sorted.find((e) => e._id === prev._id);
        return item || prev;
      });
      return true;
    } catch (err) {
      const raw = err.response && err.response.data && err.response.data.error;
      const msg = typeof raw === "string" && raw
        ? raw
        : raw && typeof raw === "object" && raw.motivo
          ? raw.motivo
          : "Não foi possível cancelar a coleta.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, empresaId]);

  // Deep link ?abrir=<id>: abre o modal completo assim que a lista carrega e
  // limpa a query p/ um refresh não reabrir.
  useEffect(() => {
    const abrir = abrirRef.current;
    if (!abrir || loading || !empresaId || !loadedOnceRef.current) return;
    abrirRef.current = null;
    const found = embarques.find((e) => e._id === abrir) || { _id: abrir };
    handleOpenFull(found);
    history.replace({ pathname: history.location.pathname, search: "" });
  }, [embarques, loading, empresaId, handleOpenFull, history]);

  const handleClearFilters = useCallback(() => {
    setFilterSearch("");
    setFilterStatus("");
    setFilterTransportadora("");
    setFilterUF("");
    setFilterDataInicio("");
    setFilterDataFim("");
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterDataFim,
    filterDataInicio,
    filterSearch,
    filterStatus,
    filterTransportadora,
    filterUF,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content lc-page">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  const filteredCountLabel = `${filteredEmbarques.length} ${
    filteredEmbarques.length === 1 ? "cotação encontrada" : "cotações encontradas"
  }`;
  const pageStart = filteredEmbarques.length === 0 ? 0 : (currentPage - 1) * LIST_PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * LIST_PAGE_SIZE, filteredEmbarques.length);

  const listDescription = loading
    ? "Atualizando as cotações cadastradas."
    : hasActiveFilters
      ? `${filteredCountLabel} para o recorte atual.`
      : `${embarques.length} cotações disponíveis para triagem operacional.`;

  return (
    <div className="content lc-page">
      <Card className="card-everlog-shell lc-hero" style={heroShellStyle}>
        <CardBody className="lc-hero__body" style={autopilotBodyStyle}>
          <div className="lc-hero__intro">
            <span className="lc-hero__eyebrow">Negociação</span>
            <h2 className="lc-hero__title">Listagem de cotações</h2>
            <p className="lc-hero__description">
              Acompanhe a operação em uma visão única de triagem, filtros rápidos e detalhe
              lateral.
            </p>
          </div>

          <div className="lc-hero__actions">
            <Button
              className="btn-sm lc-toolbar-btn lc-toolbar-btn--neutral"
              onClick={loadEmbarques}
              disabled={loading}
            >
              {loading ? "Carregando..." : "Atualizar"}
            </Button>
            <Button
              className="btn-sm lc-toolbar-btn lc-toolbar-btn--combinado"
              onClick={() => setCombinadoOpen(true)}
              disabled={!empresaId}
            >
              + Frete Combinado
            </Button>
            <Button
              className="btn-sm lc-toolbar-btn lc-toolbar-btn--primary"
              onClick={() => history.push("/app/gobid/operacao/nova-cotacao")}
            >
              + Nova Cotação
            </Button>
          </div>
        </CardBody>
      </Card>

      <SectionShell
        className="lc-filters-shell"
        actions={
          hasActiveFilters ? (
            <button type="button" className="lc-link-button" onClick={handleClearFilters}>
              Limpar filtros
            </button>
          ) : null
        }
      >
        <div className="lc-filters-stack">
          <div className="lc-status-grid">
            {QUOTE_STATUS_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`lc-status-card${filterStatus === value ? " is-active" : ""}`}
                onClick={() => setFilterStatus((prev) => (prev === value ? "" : value))}
              >
                <div
                  className="lc-status-card-dot"
                  style={{ background: STATUS_COLORS[value] }}
                />
                <div className="lc-status-card-copy">
                  <div className="lc-status-card-label">{label}</div>
                  <div className="lc-status-card-count">{statusCounts[value] || 0}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="lc-filters-panel">
            <Row className="align-items-end">
              <Col lg="3" md="6" className="mb-3 mb-lg-0">
                <label className="lc-filter-label">Busca livre</label>
                <Input
                  type="text"
                  className="lc-filter-input"
                  placeholder="Buscar nº da cotação, embarque, cliente, cidade..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                />
              </Col>
              <Col lg="2" md="6" className="mb-3 mb-lg-0">
                <label className="lc-filter-label">Transportadora</label>
                <Input
                  type="text"
                  className="lc-filter-input"
                  placeholder="Transportadora"
                  value={filterTransportadora}
                  onChange={(e) => setFilterTransportadora(e.target.value)}
                />
              </Col>
              <Col lg="1" md="6" className="mb-3 mb-lg-0">
                <label className="lc-filter-label">UF</label>
                <Input
                  type="text"
                  className="lc-filter-input"
                  placeholder="UF"
                  value={filterUF}
                  maxLength={2}
                  onChange={(e) =>
                    setFilterUF(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))
                  }
                />
              </Col>
              <Col lg="2" md="6" className="mb-3 mb-lg-0">
                <label className="lc-filter-label">Data início</label>
                <Input
                  type="date"
                  className="lc-filter-input"
                  value={filterDataInicio}
                  onChange={(e) => setFilterDataInicio(e.target.value)}
                />
              </Col>
              <Col lg="2" md="6" className="mb-0">
                <label className="lc-filter-label">Data fim</label>
                <Input
                  type="date"
                  className="lc-filter-input"
                  value={filterDataFim}
                  onChange={(e) => setFilterDataFim(e.target.value)}
                />
              </Col>
            </Row>
          </div>
        </div>
      </SectionShell>

      <div className="lc-layout">
        <div className="lc-list-col">
          <SectionShell
            className="lc-list-shell"
            eyebrow="Operação"
            title="Cotações"
            description={listDescription}
          >
            <div className="lc-list-stack">
              {loading && (
                <div className="lc-empty lc-empty--panel">
                  <div className="lc-empty-title">Carregando...</div>
                </div>
              )}

              {!loading && filteredEmbarques.length === 0 && (
                <div className="lc-empty lc-empty--panel">
                  <div className="lc-empty-title">Nenhuma cotação encontrada</div>
                  {embarques.length > 0
                    ? "Tente ajustar os filtros."
                    : "Importe cotações ou crie uma nova cotação manual."}
                </div>
              )}

              {!loading &&
                paginatedEmbarques.map((emb) => (
                  <CotacaoListItem
                    key={emb._id}
                    emb={emb}
                    selected={selectedEmb?._id === emb._id}
                    onSelect={() =>
                      setSelectedEmb((prev) => (prev?._id === emb._id ? null : emb))
                    }
                    onOpen={handleAbrir}
                    onCotar={handleCotar}
                    onLeiloar={handleLeiloar}
                    leilaoAtivo={leilaoAtivoDe(emb)}
                  />
                ))}

              {!loading && filteredEmbarques.length > LIST_PAGE_SIZE && (
                <div className="lc-pagination">
                  <div className="lc-pagination__info">
                    Mostrando {pageStart}-{pageEnd} de {filteredEmbarques.length}
                  </div>
                  <div className="lc-pagination__controls">
                    <button
                      type="button"
                      className="lc-pagination__btn"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </button>
                    {visiblePages.map((page) => (
                      <button
                        key={page}
                        type="button"
                        className={`lc-pagination__btn lc-pagination__page${page === currentPage ? " is-active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="lc-pagination__btn"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          </SectionShell>
        </div>

        <div className="lc-panel-col">
          <FinancialPanel emb={selectedEmb} onOpen={handleOpenFull} />
        </div>
      </div>

      <EditCotacaoModal
        isOpen={!!editEmb}
        onClose={handleCloseEdit}
        emb={editEmb}
        onSave={handleSaveEdit}
        onCreateVersion={handleCreateVersion}
        onDelete={handleDeleteEmbarque}
        saving={saving}
        isAprovador={isAprovador}
        onAprovar={handleAprovar}
        onRecusar={handleRecusar}
        onSolicitarColeta={handleSolicitarColeta}
        onCancelarColeta={handleCancelarColeta}
        onLeiloar={handleLeiloar}
        leilaoAtivo={leilaoAtivoDe(editEmb)}
        focoInicial={focoEdit}
      />

      <FreteCombinadoModal
        open={combinadoOpen}
        onClose={() => setCombinadoOpen(false)}
        empresaId={empresaId}
        onSaved={(created) => {
          // PO1-2863: nº da cotação no toast (é o que o embarcador informa no ERP).
          const numero = created && created.numeroCotacao != null ? created.numeroCotacao : null;
          addToast({
            place: "tr",
            type: "success",
            icon: "tim-icons icon-check-2",
            title: "Frete combinado registrado",
            description: numero != null
              ? `Cotação nº ${numero} salva com status Aprovado.`
              : "Cotação salva com status Aprovado.",
          });
          loadEmbarques();
        }}
        onError={(msg) =>
          addToast({
            place: "tr",
            type: "danger",
            icon: "tim-icons icon-alert-circle-exc",
            title: "Erro ao registrar",
            description: msg,
          })
        }
      />

    </div>
  );
};

export default ListagemCotacoes;
